import { STAR_WIDTH, STAR_HEIGHT, CENTER_PADDING, MIN_DISTANCE, EDGE_MARGIN } from '../constants/starConstants';

// Centralized function for generating star scale
export function getRandomStarScale() {
    return 0.9 + Math.random() * 0.6; // Min 0.9, Max 1.5
}

// Estimate the expanded height of a message based on its length
export function estimateExpandedMessageHeight(message, scale = 1) {
    if (!message) return 80 * scale; // Increased minimum height for stars without messages
    
    // Calculate number of characters per line
    const avgCharWidth = 8; // Average character width in pixels
    const containerWidth = 300; // Fixed width as per StarPoint component
    const charsPerLine = Math.floor(containerWidth / avgCharWidth);
    
    // Calculate estimated number of lines needed
    const messageLength = message.length;
    const estimatedLines = Math.ceil(messageLength / charsPerLine);
    
    // Calculate base height using line height of 24px
    const lineHeight = 24;
    const textHeight = estimatedLines * lineHeight;
    
    // Add more padding for the expanded state
    const padding = 40;
    
    // Scale the height with a more aggressive factor for longer messages
    const scaleFactor = messageLength > 150 ? 0.4 : 0.35;
    return (textHeight + padding) * (1 + (scale - 1) * scaleFactor);
}

// Calculate the full rectangular footprint of a star including its expanded message
export function getStarRectangle(position, scale, message) {
    const starWidth = STAR_WIDTH * scale;
    const starHeight = STAR_HEIGHT * scale;
    
    // Calculate expanded message dimensions with more accurate height estimation
    const messageLength = message?.length || 0;
    const avgCharWidth = 8; // Average character width in pixels
    const containerWidth = Math.max(200, 280 * Math.min(scale, 1)); // Base width scaled down for smaller stars
    const charsPerLine = Math.floor(containerWidth / avgCharWidth);
    const estimatedLines = Math.ceil(messageLength / charsPerLine);
    
    // Calculate base height with more generous line height and padding
    const lineHeight = 24; // Line height in pixels
    const headerHeight = 60 * Math.min(scale, 1); // Header height (name + weeks + padding)
    const padding = 60; // Increased padding for expanded state
    
    // Calculate message height with more accurate scaling
    const baseMessageHeight = Math.max(
        (estimatedLines * lineHeight),
        messageLength > 0 ? 120 : 60 // Minimum height for messages
    );
    const scaleFactor = messageLength > 150 ? 0.5 : 0.4;
    const messageHeight = baseMessageHeight * (1 + (scale - 1) * scaleFactor);
    
    // Add extra buffer for very long messages and expanded state
    const heightBuffer = Math.max(
        40,
        messageLength > 200 ? 80 : 60,
        Math.floor(messageLength / 100) * 20 // Additional buffer for very long messages
    );
    const totalMessageHeight = messageHeight + heightBuffer + padding;
    
    // Calculate asymmetric offsets for the message box
    const leftOffset = 20 * Math.min(scale, 1); // Small offset for vertical line
    const rightOffset = containerWidth - leftOffset; // Most width goes to the right
    
    // Calculate the full rectangle with more precise positioning
    return {
        left: position.x - leftOffset,
        right: position.x + rightOffset,
        top: position.y - totalMessageHeight - headerHeight,
        bottom: position.y + starHeight / 2,
        width: containerWidth,
        height: totalMessageHeight + headerHeight + starHeight / 2,
        centerX: position.x,
        centerY: position.y,
        messageHeight: totalMessageHeight,
        headerHeight: headerHeight,
        estimatedLines: estimatedLines
    };
}

// Check if two rectangles intersect (with optional buffer)
export function doRectanglesIntersect(rect1, rect2, buffer = 10) {
    return !(
        rect1.right + buffer < rect2.left - buffer ||
        rect1.left - buffer > rect2.right + buffer ||
        rect1.bottom + buffer < rect2.top - buffer ||
        rect1.top - buffer > rect2.bottom + buffer
    );
}

// Calculate the amount of overlap between two rectangles
function calculateOverlapArea(rect1, rect2) {
    // If they don't intersect, overlap is 0
    if (rect1.right < rect2.left || rect1.left > rect2.right || 
        rect1.bottom < rect2.top || rect1.top > rect2.bottom) {
        return 0;
    }
    
    // Calculate overlap dimensions
    const overlapWidth = Math.min(rect1.right, rect2.right) - Math.max(rect1.left, rect2.left);
    const overlapHeight = Math.min(rect1.bottom, rect2.bottom) - Math.max(rect1.top, rect2.top);
    
    return overlapWidth * overlapHeight;
}

// Divide the screen into vertical zones for better distribution
function getVerticalZone(y, viewportHeight, numZones = 5) {
    const zoneHeight = viewportHeight / numZones;
    return Math.floor(y / zoneHeight);
}

function isValidPosition(point, existingPoints, constraintReduction = 0, leftEdgeMargin = EDGE_MARGIN, starMessage = "", allStarData = [], topEdgeMargin = EDGE_MARGIN, bottomEdgeMargin = EDGE_MARGIN) {
    const scale = point.scale || getRandomStarScale();
    const candidateRect = getStarRectangle(point, scale, starMessage);
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Use minimal base margin
    const baseMargin = Math.max(20, Math.min(viewportWidth * 0.02, 40));
    
    // Simple margin calculations
    const effectiveMargin = baseMargin * (1 - constraintReduction * 0.5);
    
    // Check viewport boundaries
    if (candidateRect.left < effectiveMargin || 
        candidateRect.right > viewportWidth - effectiveMargin ||
        candidateRect.top < effectiveMargin || 
        candidateRect.bottom > viewportHeight - effectiveMargin) {
        return false;
    }

    // Check for overlaps with minimal buffer
    return !existingPoints.some((existing) => {
        if (!existing || !existing.x || !existing.y) return false;
        
        const existingMessage = existing.star?.message || "";
        const existingRect = getStarRectangle(existing, existing.scale || scale, existingMessage);
        
        // Use minimal buffer for collision detection
        const buffer = Math.max(10, Math.min(candidateRect.width, existingRect.width) * 0.1);
        
        // Simple rectangle intersection check
        return !(
            candidateRect.right + buffer < existingRect.left - buffer ||
            candidateRect.left - buffer > existingRect.right + buffer ||
            candidateRect.bottom + buffer < existingRect.top - buffer ||
            candidateRect.top - buffer > existingRect.bottom + buffer
        );
    });
}

export function generateStarPositions(numStars, starData = [], maxVisibleStars = null, existingPositions = []) {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Initialize arrays
    const positions = [];
    const unplacedIndices = new Set();
    
    // Use minimal margins (1% of viewport)
    const marginPercent = 0.01;
    const LEFT_EDGE_MARGIN = Math.max(10, viewportWidth * marginPercent);
    const RIGHT_EDGE_MARGIN = Math.max(10, viewportWidth * marginPercent);
    const TOP_EDGE_MARGIN = Math.max(15, viewportHeight * marginPercent);
    const BOTTOM_EDGE_MARGIN = Math.max(10, viewportHeight * marginPercent);
    
    // Calculate usable area
    const usableWidth = viewportWidth - (LEFT_EDGE_MARGIN + RIGHT_EDGE_MARGIN);
    const usableHeight = viewportHeight - (TOP_EDGE_MARGIN + BOTTOM_EDGE_MARGIN);
    
    // Create a more granular grid system
    const GRID_COLS = 6; // Increased from 5
    const GRID_ROWS = 5; // Increased from 4
    const CELL_WIDTH = usableWidth / GRID_COLS;
    const CELL_HEIGHT = usableHeight / GRID_ROWS;
    
    //console.log(`Generating star positions with optimized space usage. Viewport: ${viewportWidth}x${viewportHeight}, Stars: ${numStars}, Grid: ${GRID_COLS}x${GRID_ROWS}, Margins: ${LEFT_EDGE_MARGIN}px`);
    
    // Try to place each star
    for (let starIndex = 0; starIndex < numStars; starIndex++) {
        const starMessage = starData[starIndex]?.message || "";
        let placed = false;
        
        // Increase attempts for better placement chances
        const totalAttempts = 300; // Increased from 200
        
        // Try multiple positions with different strategies
        for (let attempt = 0; attempt < totalAttempts && !placed; attempt++) {
            const scale = getRandomStarScale();
            
            // More aggressive constraint reduction that starts earlier
            const constraintReduction = Math.min(0.8, (attempt / totalAttempts) * 1.2);
            
            // Get all grid cells
            const gridCells = [];
            for (let row = 0; row < GRID_ROWS; row++) {
                for (let col = 0; col < GRID_COLS; col++) {
                    const cellLeft = LEFT_EDGE_MARGIN + (col * CELL_WIDTH);
                    const cellTop = TOP_EDGE_MARGIN + (row * CELL_HEIGHT);
                    
                    // Weight cells based on multiple factors
                    const distanceFromTopLeft = Math.sqrt(row * row + col * col);
                    const distanceFromCenter = Math.abs(col - GRID_COLS/2) + Math.abs(row - GRID_ROWS/2);
                    const weight = (1 + distanceFromTopLeft * 0.3) * (1 - distanceFromCenter * 0.1);
                    
                    gridCells.push({
                        row,
                        col,
                        weight,
                        center: {
                            x: cellLeft + (CELL_WIDTH / 2),
                            y: cellTop + (CELL_HEIGHT / 2)
                        }
                    });
                }
            }
            
            // Sort cells by weight and add randomization
            gridCells.sort((a, b) => {
                const randomFactor = Math.random() * 0.4; // 40% randomness
                const aScore = a.weight * (1 + randomFactor);
                const bScore = b.weight * (1 + randomFactor);
                return bScore - aScore;
            });
            
            // Try each cell with increased movement range
            for (const cell of gridCells) {
                // Increase movement range as attempts increase
                const randomRange = Math.min(1.2, 0.9 + (attempt / totalAttempts) * 0.3);
                const x = cell.center.x + (Math.random() - 0.5) * CELL_WIDTH * randomRange;
                const y = cell.center.y + (Math.random() - 0.5) * CELL_HEIGHT * randomRange;
                
                const candidate = { x, y, scale, star: starData[starIndex] };
                
                // Reduce collision buffer as attempts increase
                const collisionBuffer = Math.max(3, 10 * (1 - constraintReduction));
                
                if (isValidPosition(candidate, positions, constraintReduction,
                                  LEFT_EDGE_MARGIN, starMessage, starData,
                                  TOP_EDGE_MARGIN, BOTTOM_EDGE_MARGIN)) {
                    positions.push(candidate);
                    placed = true;
                    break;
                }
            }
            
            // If we're getting desperate (75% through attempts), try more aggressive placement
            if (!placed && attempt > totalAttempts * 0.75) {
                const x = LEFT_EDGE_MARGIN + Math.random() * usableWidth;
                const y = TOP_EDGE_MARGIN + Math.random() * usableHeight;
                const candidate = { 
                    x, 
                    y, 
                    scale: 0.9,  // Use minimum scale
                    star: starData[starIndex] 
                };
                
                if (isValidPosition(candidate, positions, 0.9)) {
                    positions.push(candidate);
                    placed = true;
                }
            }
        }
        
        if (!placed) {
            unplacedIndices.add(starIndex);
            //console.log(`Failed to place star ${starIndex} with message length ${starMessage.length}`);
        }
    }
    
    //console.log('Placement results:', {
    //    totalStars: numStars,
    //    placedStars: positions.length,
    //    unplacedStars: unplacedIndices.size,
    //    existingStars: existingPositions.length
    //});

    return {
        positions,
        unplacedIndices: Array.from(unplacedIndices)
    };
} 